




























































































import Vue from "vue";

import IconUserAvatar from "@joyz/app-core/assets/icons/default/User.svg";
import { AudioPlayerPlayOptions } from "@joyz/app-core/models/audio";
import { AudioDecoderPlayer } from "@joyz/app-core/utils/audio";
import { vueScrollToBottom } from "@joyz/app-core/utils/styles";

import {
  ChatAllDisplayableObject,
  ApiIsChatBubble,
  ApiHasAudios,
  ApiChatImageUrl
} from "@tt-app/models/exercise/common";
import { MainLogger } from "@tt-app/utils/internal/logger";

import ChatDictationSpeaker from "./ChatDictationSpeaker.vue";
import FeedbackPopup from "@tt-app/components/feedback/FeedbackPopup.vue";
import { audioContext } from "@tt-app/utils/audio/audioContext";

/**
 * Convenience to have a local mapping type-provided-by-the-server and
 * type-which-works-with-our-components
 */
type LocalChatItemType =
  | "MSG" // Standard ChatMessage
  | "IMG_STD" // Simple Image display
  | "IMG_PB" // Image problem with dialog
  | "SEP" // Chat separator
  | "DICT_SPEAKER" // Dictation speaker
  | "DICT_ANSWER" // Dictation user answer
  | "DICT_CORRECTION" // Dictation correction, only visible when chat is finished
  | undefined;

interface Props {
  // AudioPlayer is expected to be instantiated at parent level to have
  // the proper user interaction to keep the audio context running
  audioDecoderPlayer: AudioDecoderPlayer;
  // If true, scroll down to the bottom on new item
  autoScroll: boolean;
  chatAllowInteraction: boolean;
  chatItems: ChatAllDisplayableObject[];
  chatItemPlayIndex: number;
  chatFinished: boolean;
  logId: string;
}

interface Data {
  currentItemPlaying: number;
  feedbackFormVisible: boolean;
}

interface Methods {
  getAudioStatus: (msg: ApiHasAudios, idx: number) => "none" | "idle" | "playing" | "blocked";
  getAvatar: (msg: ApiIsChatBubble) => string | undefined;
  getImgUrl: (urls: ApiChatImageUrl[]) => string;
  getItemType: (item: ChatAllDisplayableObject) => LocalChatItemType;
  handleFeedbackClose: () => void;
  playAudio: (idx: number, options?: AudioPlayerPlayOptions) => Promise<void>;
  stopAudio: () => void;
  scrollBodyDown: () => Promise<void>;
}

interface Computed {
  showFeedbackForm: boolean;
}

/**
 * @emits audio-play(item)
 * @emits image-opened(item)
 * @emits image-closed(item)
 */
export default Vue.extend<Data, Methods, Computed, Props>({
  name: "ChatBody",
  components: { FeedbackPopup, ChatDictationSpeaker },

  props: {
    audioDecoderPlayer: { type: Object as () => AudioDecoderPlayer, default: undefined },
    autoScroll: { type: Boolean, default: false },
    chatAllowInteraction: { type: Boolean, default: true },
    chatItems: { type: Array, required: true },
    chatFinished: { type: Boolean, default: false },
    chatItemPlayIndex: { type: Number, default: -1 },
    logId: { type: String, default: undefined }
  },

  data() {
    return {
      currentItemPlaying: -1,
      feedbackFormVisible: true
    };
  },

  watch: {
    chatItemPlayIndex: {
      handler() {
        this.currentItemPlaying = this.chatItemPlayIndex;
      }
    },
    chatItems: {
      // newItems: ChatAllDisplayableObject[]
      async handler() {
        if (this.autoScroll) {
          this.scrollBodyDown();
        }
      }
    }
  },

  beforeDestroy() {
    this.audioDecoderPlayer.stop();
  },

  computed: {
    showFeedbackForm() {
      return this.feedbackFormVisible && this.chatFinished;
    }
  },

  methods: {
    async playAudio(idx: number, options: AudioPlayerPlayOptions = {}) {
      if (!this.chatAllowInteraction) {
        return;
      }

      // const item = this.visibleItems[idx];
      const item = this.chatItems[idx];

      if (item && (item as ApiHasAudios).audioUrls) {
        // this.audioDecoderPlayer.stop();

        // For tracking in dictation exercise T_T
        this.$emit("audio-play", item);

        this.currentItemPlaying = idx;
        try {
          await audioContext.resume();
          this.audioDecoderPlayer.play((item as ApiHasAudios).audioUrls, {
            ...options,
            onEnded: () => {
              if (options.onEnded) options.onEnded();
              this.currentItemPlaying = -1;
            }
          });
        } catch (err) {
          // do something
        }
      } else if (options.onEnded) {
        MainLogger.warn(`No audio found for item ${JSON.stringify(item)}`);
        options.onEnded();
      }
    },

    stopAudio() {
      this.audioDecoderPlayer.stop();
    },

    getAudioStatus(msg, idx) {
      if (this.currentItemPlaying === idx) {
        return "playing";
      }

      if (!msg.audioUrls || !msg.audioUrls.length) {
        return "none";
      }

      if (!this.chatAllowInteraction) {
        return "blocked";
      }

      return "idle";
    },

    getAvatar(msg) {
      // Defined by server
      if (msg.avatar) {
        return msg.avatar;
      }
      // Placeholder for users only
      if (msg.side === "right") {
        return msg.avatar || IconUserAvatar;
      }

      return undefined;
    },

    getItemType(item) {
      switch (item.type) {
        case "PLAIN_TEXT":
          return "SEP";
        case "CHAT_CONVERSATION_USER_ANSWER":
        case "CHAT_MESSAGE":
        case "CHAT_TEXT":
          return "MSG";
        case "CHAT_IMAGE":
          return item.timeLimit ? "IMG_PB" : "IMG_STD";
        case "CHAT_DICTATION_USER_SPEAKER":
          return "DICT_SPEAKER";
        case "CHAT_DICTATION_USER_ANSWER":
          return "DICT_ANSWER";
        case "CHAT_DICTATION_CORRECTION":
          return this.chatFinished ? "DICT_CORRECTION" : undefined;
      }
    },

    getImgUrl(urls) {
      return urls[0].url;
    },

    handleFeedbackClose() {
      this.feedbackFormVisible = false;
    },

    async scrollBodyDown() {
      await vueScrollToBottom(this);
      // console.log(`${this.$el.scrollHeight} / ${this.$el.clientHeight} > ${this.$el.scrollTop}`);
    }
  }
});
